import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api";

/* =====================================================
   ASYNC THUNK
   ===================================================== */

/**
 * Fetch all active outbound workspaces along with
 * their available filter fields from backend.
 *
 * INPUT:
 * - No arguments (uses logged-in user context on backend)
 *
 * OUTPUT:
 * - On success: Array of workspaces with parsed fields
 * - On failure: Reject with error message
 */
export const fetchOutboundWorkspaces = createAsyncThunk(
  "outboundVoice/fetchWorkspaces",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.manageUser.getOutboundWorkspacesWithFields();
      console.log("Fetched Outbound Workspaces:", res);

      // Validate response structure
      if (!res) {
        return rejectWithValue("No response from server");
      }

      if (!res.status) {
        return rejectWithValue("Failed to load outbound workspaces");
      }

      if (!res.data) {
        console.warn("fetchOutboundWorkspaces: Response missing data field");
        return [];
      }

      // Validate data is an array
      if (!Array.isArray(res.data)) {
        console.error("fetchOutboundWorkspaces: data is not an array:", typeof res.data);
        return [];
      }

      // Filter out invalid workspace entries
      const validWorkspaces = res.data.filter(ws =>
        ws &&
        typeof ws === 'object' &&
        ws.workspace_id &&
        ws.workspace_name
      );

      if (validWorkspaces.length !== res.data.length) {
        console.warn(`fetchOutboundWorkspaces: Filtered out ${res.data.length - validWorkspaces.length} invalid workspaces`);
      }

      // Validate parsed_fields for each workspace
      const workspacesWithValidFields = validWorkspaces.map(ws => ({
        ...ws,
        parsed_fields: Array.isArray(ws.parsed_fields)
          ? ws.parsed_fields.filter(field =>
              field &&
              typeof field === 'object' &&
              field.key &&
              typeof field.key === 'string' &&
              field.key.trim() !== ''
            )
          : []
      }));

      return workspacesWithValidFields;

    } catch (err) {
      console.error("fetchOutboundWorkspaces error:", err);
      return rejectWithValue(err.message || "Unknown error occurred");
    }
  }
);

/* =====================================================
   INITIAL STATE
   ===================================================== */

const initialState = {
  // Master toggle for outbound voice feature
  enabled: false,

  /* ---------- Global Cache ---------- */
  // All workspaces returned from backend
  workspaces: [],
  loading: false,
  error: null,

  /* ---------- User Selections ---------- */

  // List of selected workspace IDs
  selectedWorkspaces: [],

  // workspaceId -> [available filter fields]
  parsedFields: {},

  // workspaceId -> { fieldName -> selectedValues[] }
  selectedValues: {},

  // workspaceId -> boolean (true = required, false = optional)
  requiredSettings: {},
};

/* =====================================================
   SLICE
   ===================================================== */

const outboundVoiceSlice = createSlice({
  name: "outboundVoice",
  initialState,

  /* ---------- SYNC REDUCERS ---------- */
  reducers: {
    /**
     * Enable or disable outbound voice feature.
     * NOTE:
     * - Does NOT clear selections
     * - Allows user to toggle off/on without losing data
     */
    toggleOutbound(state, action) {
      state.enabled = action.payload;
    },

    /**
     * Set selected workspaces.
     * Also cleans up data for unselected workspaces
     * to avoid stale state.
     */
    setWorkspaces(state, action) {
      state.selectedWorkspaces = action.payload;

      // Remove data for deselected workspaces
      Object.keys(state.parsedFields).forEach((wsId) => {
        if (!action.payload.includes(Number(wsId))) {
          delete state.parsedFields[wsId];
          delete state.selectedValues[wsId];
          delete state.requiredSettings[wsId];
        }
      });
    },

    /**
     * Store parsed filter fields for a workspace
     *
     * INPUT:
     * - workspaceId
     * - fields[] (available filter fields)
     */
    setParsedFields(state, action) {
      const { workspaceId, fields } = action.payload;
      state.parsedFields[workspaceId] = fields;
      const sv = state.selectedValues[workspaceId];
      if (sv && typeof sv === "object") {
        Object.keys(sv).forEach((fieldKey) => {
          if (!fields.includes(fieldKey)) {
            delete sv[fieldKey];
          }
        });
      }
    },

    /**
     * Store selected values for a specific field
     * under a workspace.
     *
     * INPUT:
     * - workspaceId
     * - field (filter name)
     * - values[] (selected filter values)
     */
    setSelectedValues(state, action) {
      const { workspaceId, field, values } = action.payload;

      if (!state.selectedValues[workspaceId]) {
        state.selectedValues[workspaceId] = {};
      }

      state.selectedValues[workspaceId][field] = values;
    },

    /**
     * Set whether filter matching is required
     * for a given workspace.
     *
     * INPUT:
     * - workspaceId
     * - value (boolean)
     */
    setRequiredSetting(state, action) {
      const { workspaceId, value } = action.payload;
      state.requiredSettings[workspaceId] = value;
    },

    /**
     * Hydrate outbound state (used when editing
     * existing outbound webhook configuration).
     */
    hydrateOutbound(state, action) {
      return { ...state, ...action.payload };
    },

    /**
     * Reset entire outbound voice state
     * (used on modal close / save success).
     */
    resetOutbound() {
      return { ...initialState };
    },
  },

  /* ---------- ASYNC REDUCERS ---------- */
  extraReducers: (builder) => {
    builder
      .addCase(fetchOutboundWorkspaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOutboundWorkspaces.fulfilled, (state, action) => {
        state.loading = false;
        state.workspaces = action.payload;
      })
      .addCase(fetchOutboundWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

/* =====================================================
   EXPORTS
   ===================================================== */

export const {
  toggleOutbound,
  setWorkspaces,
  setParsedFields,
  setSelectedValues,
  setRequiredSetting,
  hydrateOutbound,
  resetOutbound,
} = outboundVoiceSlice.actions;

export default outboundVoiceSlice.reducer;
